import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { selectAllowCommercialBreak, setAllowCommercialBreak } from 'src/slices/game.slice';
import { selectInspectorEvents } from 'src/slices/gameEvents.slice';
import { openNotification } from 'src/slices/notification.slice';

import CheckboxInput from 'src/components/ui/CheckboxInput';
import DeviceSwitch from 'src/components/ui/DeviceSwitch';
import TimeToggle from 'src/components/ui/TimeToggle';
import MuteToggle from 'src/components/ui/MuteToggle';
import AssetsDownload from 'src/components/ui/AssetsDownload';
import SettingsMenu from 'src/components/ui/SettingsMenu';

import { isTimeExperimentEnabled, isAssetsExperimentEnabled, isMuteExperimentEnabled } from 'src/utils/url';

const UtilitiesModuleWrapper = styled.section`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border-radius: 8px;
    background: var(--surface);
`;

const UtilitiesControls = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;

const UtilitiesDivider = styled.div`
    width: 1px;
    height: 32px;
    background: var(--border-faint);
`;

const SendSDKPostMessage = ({ type }) => {
    const iframe = document.querySelector('iframe');

    if (!iframe) {
        console.error('No iframe found!');
        return;
    }

    iframe.contentWindow.postMessage({ type }, '*');
};

export default function UtilitiesModule() {
    const dispatch = useDispatch();

    const allowCommercialBreak = useSelector(selectAllowCommercialBreak);
    const inspectorEvents = useSelector(selectInspectorEvents);

    const handleAllowCommercialBreak = (checked) => {
        dispatch(setAllowCommercialBreak({ toggle: checked }));

        SendSDKPostMessage({
            type: checked ? 'pokiInspectorAllowCommercialBreak' : 'pokiInspectorDisallowCommercialBreak',
        });
    };

    useEffect(() => {
        if (inspectorEvents.length === 0) {
            return;
        }

        const latestInspectorEvent = inspectorEvents[inspectorEvents.length - 1];

        if (latestInspectorEvent.event === 'localStorageCleared') {
            window.location.reload();
            return;
        }

        if (latestInspectorEvent.event === 'localStorageClearError') {
            dispatch(openNotification({
                id: 'local-storage-clear-error',
                type: 'error',
                message: 'Failed to clear localStorage. Please try again.',
            }));
        }
    }, [dispatch, inspectorEvents]);

    return (
        <UtilitiesModuleWrapper>
            <UtilitiesControls>
                <DeviceSwitch />
                <UtilitiesDivider />
                <CheckboxInput
                    id="allow-commercial-break"
                    label="Force show ads"
                    tooltip="When enabled, commercial breaks and display ads will always be shown no matter the timing or other restrictions."
                    checked={allowCommercialBreak}
                    onChange={handleAllowCommercialBreak}
                />
                {isTimeExperimentEnabled() && (
                    <>
                        <UtilitiesDivider />
                        <TimeToggle />
                    </>
                )}
                {isAssetsExperimentEnabled() && (
                    <>
                        <UtilitiesDivider />
                        <AssetsDownload />
                    </>
                )}
                {isMuteExperimentEnabled() && (
                    <>
                        <UtilitiesDivider />
                        <MuteToggle />
                    </>
                )}
            </UtilitiesControls>
            <SettingsMenu />
        </UtilitiesModuleWrapper>
    );
}
